import React from 'react';
import { useNavigate } from 'react-router-dom';
import { SubscriptionTier } from '../../types/subscription';
import './UpgradePromptModal.css';

/**
 * Props for the UpgradePromptModal component
 */
export interface UpgradePromptModalProps {
  /** Controls modal visibility */
  isOpen: boolean;
  
  /** Callback to close modal */
  onClose: () => void;
  
  /** Type of limit that was exceeded */
  limitType: 'crates' | 'storage' | 'shares' | 'secrets';
  
  /** Current usage amount */
  currentUsage: number;
  
  /** Tier limit */
  limit: number;
  
  /** User's current tier */
  currentTier: SubscriptionTier;
}

/**
 * Tier information for comparison display
 */
interface TierComparison {
  name: SubscriptionTier;
  cratesLimit: number;
  storageLimit: number;
  sharesLimit: number;
  secretsLimit: number;
  priceMonthly: number;
}

/**
 * UpgradePromptModal Component
 * 
 * Displays when users hit their tier limits, showing:
 * - The specific limit that was exceeded
 * - Current usage vs. limit
 * - Tier comparison
 * - Link to subscription page for upgrade
 * 
 * Requirements: 1.7, 6.4, 9.6
 */
const UpgradePromptModal: React.FC<UpgradePromptModalProps> = ({
  isOpen,
  onClose,
  limitType,
  currentUsage,
  limit,
  currentTier
}) => {
  const navigate = useNavigate();
  // Don't render if modal is not open
  if (!isOpen) {
    return null;
  }

  // Tier comparison data
  const tiers: TierComparison[] = [
    {
      name: 'Free',
      cratesLimit: 2,
      storageLimit: 5,
      sharesLimit: 2,
      secretsLimit: 1,
      priceMonthly: 0
    },
    {
      name: 'Pro',
      cratesLimit: 50,
      storageLimit: 50,
      sharesLimit: 100,
      secretsLimit: 10,
      priceMonthly: 10
    },
    {
      name: 'Enterprise',
      cratesLimit: 500,
      storageLimit: 500,
      sharesLimit: 1000,
      secretsLimit: 50,
      priceMonthly: 50
    }
  ];

  /**
   * Format storage size from bytes to GB
   */
  const formatStorage = (bytes: number): string => {
    const gb = bytes / (1024 * 1024 * 1024);
    return gb.toFixed(2);
  };

  /**
   * Get display value based on limit type
   */
  const getDisplayValue = (value: number): string => {
    if (limitType === 'storage') {
      return `${formatStorage(value)} GB`;
    }
    return value.toString();
  };

  /**
   * Get limit type display name
   */
  const getLimitTypeName = (): string => {
    switch (limitType) {
      case 'crates':
        return 'Crates';
      case 'storage':
        return 'Storage';
      case 'shares':
        return 'Shares';
      case 'secrets':
        return 'Secrets';
      default:
        return 'Resource';
    }
  };

  /**
   * Get the appropriate limit value for a tier based on limit type
   */
  const getTierLimit = (tier: TierComparison): number => {
    switch (limitType) {
      case 'crates':
        return tier.cratesLimit;
      case 'storage':
        return tier.storageLimit;
      case 'shares':
        return tier.sharesLimit;
      case 'secrets':
        return tier.secretsLimit;
      default:
        return 0;
    }
  };

  /**
   * Navigate to subscription page
   */
  const handleUpgrade = () => {
    onClose();
    navigate('/dashboard?view=subscription');
  };

  /**
   * Handle backdrop click to close modal
   */
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className="upgrade-modal-backdrop" 
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="upgrade-modal-title"
    >
      <div className="upgrade-modal-content">
        {/* Close button */}
        <button 
          className="upgrade-modal-close"
          onClick={onClose}
          aria-label="Close modal"
        >
          ×
        </button>

        {/* Header */}
        <div className="upgrade-modal-header">
          <div className="upgrade-modal-icon">⚠️</div>
          <h2 id="upgrade-modal-title" className="upgrade-modal-title">
            {getLimitTypeName()} Limit Reached
          </h2>
        </div>

        {/* Current usage information */}
        <div className="upgrade-modal-usage">
          <p className="upgrade-modal-message">
            You've reached your {currentTier} tier limit for {getLimitTypeName().toLowerCase()}.
          </p>
          <div className="upgrade-usage-stats">
            <div className="upgrade-usage-item">
              <span className="upgrade-usage-label">Current Usage:</span>
              <span className="upgrade-usage-value">{getDisplayValue(currentUsage)}</span>
            </div>
            <div className="upgrade-usage-item">
              <span className="upgrade-usage-label">Your Limit:</span>
              <span className="upgrade-usage-value">{getDisplayValue(limit)}</span>
            </div>
          </div>
        </div>

        {/* Tier comparison */}
        <div className="upgrade-modal-tiers">
          <h3 className="upgrade-tiers-title">Compare Plans</h3>
          <div className="upgrade-tiers-grid">
            {tiers.map((tier) => {
              const tierLimit = getTierLimit(tier);
              const isCurrentTier = tier.name === currentTier;
              const isUpgrade = tierLimit > limit;

              return (
                <div 
                  key={tier.name}
                  className={`upgrade-tier-card ${isCurrentTier ? 'current' : ''} ${isUpgrade ? 'upgrade' : ''}`}
                >
                  <div className="upgrade-tier-name">{tier.name}</div>
                  <div className="upgrade-tier-price">
                    ${tier.priceMonthly}
                    <span className="upgrade-tier-period">/mo</span>
                  </div>
                  <div className="upgrade-tier-limit">
                    <span className="upgrade-tier-limit-label">{getLimitTypeName()}:</span>
                    <span className="upgrade-tier-limit-value">
                      {limitType === 'storage' ? `${tierLimit} GB` : tierLimit}
                    </span>
                  </div>
                  {isCurrentTier && (
                    <div className="upgrade-tier-badge">Current</div>
                  )}
                  {isUpgrade && !isCurrentTier && (
                    <div className="upgrade-tier-badge recommended">Upgrade</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Action buttons */}
        <div className="upgrade-modal-actions">
          <button 
            className="upgrade-modal-button primary"
            onClick={handleUpgrade}
          >
            View Subscription Plans
          </button>
          <button 
            className="upgrade-modal-button secondary"
            onClick={onClose}
          >
            Maybe Later
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpgradePromptModal;
